import { ProjectRenderer } from '../ProjectRenderer';
import { OrbitControls } from './OrbitControls';
import { CameraType, CreatorScreenshotCamera } from '@/types/creator';
import { debounce } from 'lodash';
import { ThreeEvent } from '../types';
import { UUID } from '@/types/types';
import { Loader } from '../utils/Loader';
import {
	PerspectiveCamera,
	OrthographicCamera,
	MathUtils,
	Box3,
	Vector3,
	PerspectiveCameraJSON,
	TOUCH,
} from 'three';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import APP_CONFIG from '@/configs/appConfig';
import gsap from 'gsap';

export type SerializedCameraController = {
	userScreenshotPerspectiveCameras: PerspectiveCameraJSON[];
};

export class CameraController {
	private _activeCamera: CameraType = APP_CONFIG.DEFAULT_CAMERA;
	private loader = Loader.getInstance();
	perspectiveCamera!: PerspectiveCamera;
	orthographicCamera!: OrthographicCamera;
	screenshotOrthographicCamera!: OrthographicCamera;
	screenshotPerspectiveCameras: PerspectiveCamera[] = [];
	userScreenshotPerspectiveCameras: PerspectiveCamera[] = [];
	perspectiveControls!: OrbitControls;
	orthographicControls!: OrbitControls;

	constructor(private renderer: ProjectRenderer) {
		this.init();
	}

	get activeCamera() {
		return this._activeCamera;
	}

	set activeCamera(camera: CameraType) {
		this._activeCamera = camera;
		this.centerCamera();

		this.renderer.setAvailability();
	}

	private init() {
		this.createCameras();
		this.createControls();
	}

	private createCameras() {
		const aspect =
			this.renderer.canvas!.clientWidth / this.renderer.canvas!.clientHeight;

		this.orthographicCamera = new OrthographicCamera();
		this.orthographicCamera.layers.enableAll();
		this.orthographicCamera.position.set(0, 15, 0);
		this.orthographicCamera.lookAt(0, 0, 0);

		this.perspectiveCamera = new PerspectiveCamera();
		this.perspectiveCamera.layers.enable(
			RENDERER_CONFIG.COLLIDABLE_MODULE.LAYER
		);
		this.perspectiveCamera.aspect = aspect;
		this.perspectiveCamera.position.set(5, 5, 5);

		for (const cameraConfig of RENDERER_CONFIG.SCREENSHOT
			.PERSPECITIVE_CAMERAS) {
			const camera = new PerspectiveCamera();

			camera.layers.enable(RENDERER_CONFIG.COLLIDABLE_MODULE.LAYER);
			camera.aspect = aspect;
			camera.position.copy(cameraConfig.POSITION);
			camera.lookAt(cameraConfig.TARGET);

			this.screenshotPerspectiveCameras.push(camera);
		}

		this.screenshotOrthographicCamera = new OrthographicCamera();
		this.screenshotOrthographicCamera.layers.enable(
			RENDERER_CONFIG.COLLIDABLE_MODULE.LAYER
		);
		this.screenshotOrthographicCamera.position.set(0, 10, 0);
		this.screenshotOrthographicCamera.lookAt(0, 0, 0);

		this.screenshotOrthographicCamera.left =
			(-RENDERER_CONFIG.CAMERA.ORTHO.FRUSTRUM_SIZE * window.innerWidth) /
			window.innerHeight /
			2;
		this.screenshotOrthographicCamera.right =
			(RENDERER_CONFIG.CAMERA.ORTHO.FRUSTRUM_SIZE * window.innerWidth) /
			window.innerHeight /
			2;
		this.screenshotOrthographicCamera.top =
			RENDERER_CONFIG.CAMERA.ORTHO.FRUSTRUM_SIZE / 2;
		this.screenshotOrthographicCamera.bottom =
			-RENDERER_CONFIG.CAMERA.ORTHO.FRUSTRUM_SIZE / 2;

		this.orthographicCamera.updateProjectionMatrix();
		this.perspectiveCamera.updateProjectionMatrix();
	}

	private createControls() {
		// Orthographic Controls

		this.orthographicControls = new OrbitControls(
			this.orthographicCamera,
			this.renderer.canvas
		);
		this.orthographicControls.target.set(0, 0, 0);
		this.orthographicControls.panSpeed = RENDERER_CONFIG.CONTROLS.PAN_SPEED;
		this.orthographicControls.enableDamping = true;
		this.orthographicControls.dampingFactor = 0.05;
		this.orthographicControls.enableRotate = false;

		this.orthographicControls.touches = {
			ONE: TOUCH.PAN,
			TWO: TOUCH.DOLLY_PAN,
		};

		this.orthographicControls.minZoom =
			RENDERER_CONFIG.CAMERA.ORTHO.MAX_ZOOM_OUT;
		this.orthographicControls.maxZoom =
			RENDERER_CONFIG.CAMERA.ORTHO.MAX_ZOOM_IN;

		this.orthographicControls.addEventListener(
			'change',
			debounce(() => {
				this.renderer.emitter.emit(
					ThreeEvent.ZOOM_LEVEL,
					this.orthographicCamera.zoom
				);
			}, 100)
		);

		this.orthographicControls.saveState();

		// Perspective controls

		this.perspectiveControls = new OrbitControls(
			this.perspectiveCamera,
			this.renderer.canvas
		);
		this.perspectiveControls.target.set(0, 0, 0);

		this.perspectiveControls.maxDistance =
			RENDERER_CONFIG.CAMERA.PERSPECTIVE.MAX_DISTANCE;
		this.perspectiveControls.minDistance =
			RENDERER_CONFIG.CAMERA.PERSPECTIVE.MIN_DISTANCE;

		this.perspectiveControls.panSpeed = RENDERER_CONFIG.CONTROLS.PAN_SPEED;
		this.perspectiveControls.enableDamping = true;
		this.perspectiveControls.dampingFactor = 0.05;

		this.perspectiveControls.maxPolarAngle = MathUtils.degToRad(
			RENDERER_CONFIG.CONTROLS.MAX_POLAR_ANGLE
		);

		this.perspectiveControls.addEventListener(
			'change',
			debounce(() => {
				const distance = this.perspectiveCamera.position.distanceTo(
					this.perspectiveControls.target
				);

				this.renderer.emitter.emit(ThreeEvent.DISTANCE, distance);
			}, 150)
		);

		this.perspectiveControls.saveState();
	}

	removeUserScreenshotCamera(id: UUID) {
		this.userScreenshotPerspectiveCameras =
			this.userScreenshotPerspectiveCameras.filter(
				(camera) => camera.name !== id
			);
	}

	addUserScreenshotCamera(data: CreatorScreenshotCamera) {
		const aspect =
			this.renderer.canvas!.clientWidth / this.renderer.canvas!.clientHeight;

		const camera = new PerspectiveCamera();
		camera.name = data.id;

		camera.layers.enable(RENDERER_CONFIG.COLLIDABLE_MODULE.LAYER);
		camera.aspect = aspect;
		camera.position.copy(this.perspectiveCamera.position);
		camera.userData = {
			target: this.perspectiveControls.target.toArray(),
			name: data.name,
			id: data.id,
		};

		this.userScreenshotPerspectiveCameras.push(camera);
	}

	setFromUserScreenshotCamera(id: UUID) {
		const camera = this.userScreenshotPerspectiveCameras.find(
			(camera) => camera.userData.id === id
		);
		if (!camera) return;

		const target = new Vector3().fromArray(camera.userData.target);

		gsap.to(this.perspectiveCamera.position, {
			x: camera.position.x,
			y: camera.position.y,
			z: camera.position.z,
			duration: 0.5,
			ease: 'power1.inOut',
			onUpdate: () => {
				this.perspectiveCamera.lookAt(target);
				this.perspectiveControls.target.copy(target);
				this.perspectiveControls.update();
			},
		});
	}

	syncUserScreenshotCameras(creatorCameras: CreatorScreenshotCamera[]) {
		const newArray: PerspectiveCamera[] = [];
		for (const camera of creatorCameras) {
			const existingCamera = this.userScreenshotPerspectiveCameras.find(
				(c) => c.userData.id === camera.id
			)!;

			existingCamera.userData.name = camera.name;
			newArray.push(existingCamera);
		}
	}

	prepareScreenshotCameras() {
		if (!this.renderer.playgroundManager.floor.floor) return;

		const box3 = new Box3().setFromObject(
			this.renderer.playgroundManager.floor.floor
		);

		const center = box3.getCenter(new Vector3()),
			size = box3.getSize(new Vector3());

		const aspect =
			RENDERER_CONFIG.SCREENSHOT.WIDTH / RENDERER_CONFIG.SCREENSHOT.HEIGHT;

		const maxDim = Math.max(size.x, size.y, size.z);

		if (this.userScreenshotPerspectiveCameras.length) {
			for (const camera of this.userScreenshotPerspectiveCameras) {
				camera.aspect = aspect;
				const target = new Vector3().fromArray(camera.userData.target);
				camera.lookAt(target);
				camera.updateProjectionMatrix();
			}
		} else {
			for (const camera of this.screenshotPerspectiveCameras) {
				camera.aspect = aspect;

				const fov = MathUtils.degToRad(camera.fov);
				const cameraDistance = maxDim / (2 * Math.tan(fov / 2));
				const direction = camera.position.clone().normalize();
				const cameraPosition = center
					.clone()
					.add(direction.multiplyScalar(cameraDistance));

				camera.position.copy(cameraPosition);
				camera.lookAt(center);
				camera.updateProjectionMatrix();
			}
		}

		let width = size.x + RENDERER_CONFIG.SCREENSHOT.FLOOR_SIZE_OFFSET;
		let height = size.z + RENDERER_CONFIG.SCREENSHOT.FLOOR_SIZE_OFFSET;

		if (width / height > aspect) height = width / aspect;
		else width = height * aspect;

		const halfW = width / 2;
		const halfH = height / 2;

		this.screenshotOrthographicCamera.left = -halfW;
		this.screenshotOrthographicCamera.right = halfW;
		this.screenshotOrthographicCamera.top = halfH;
		this.screenshotOrthographicCamera.bottom = -halfH;

		this.screenshotOrthographicCamera.position.set(center.x, 10, center.y);
		this.screenshotOrthographicCamera.lookAt(center.x, 0, center.y);

		this.screenshotOrthographicCamera.updateProjectionMatrix();
	}

	centerCamera() {
		if (!this.renderer.playgroundManager.floor.floor) return;

		const box = new Box3().setFromObject(
			this.renderer.playgroundManager.floor.floor
		);
		const size = new Vector3();
		box.getSize(size);

		const length = size.length();

		this.perspectiveControls.target.set(0, 0, 0);

		this.perspectiveCamera.position.set(0, length, length);
		this.perspectiveControls.update();
	}

	handleCameraZoom(zoom: number) {
		switch (this._activeCamera) {
			case CameraType.PERSPECTIVE: {
				const target = this.perspectiveControls.target.clone();
				const currentPosition = this.perspectiveCamera.position.clone();
				const direction = new Vector3()
					.subVectors(currentPosition, target)
					.normalize();

				const newPosition = target
					.clone()
					.add(direction.clone().multiplyScalar(zoom));

				gsap.to(this.perspectiveCamera.position, {
					x: newPosition.x,
					y: newPosition.y,
					z: newPosition.z,
					duration: 0.5,
					ease: 'power1.inOut',
					onUpdate: () => {
						this.perspectiveCamera.lookAt(target);
					},
					onComplete: () => {
						this.perspectiveControls.update();
					},
				});
				break;
			}
			case CameraType.ORTHOGRAPHIC: {
				gsap.to(this.orthographicCamera, {
					zoom: zoom,
					duration: 0.4,
					ease: 'power2.out',
					onUpdate: () => {
						this.orthographicCamera.updateProjectionMatrix();
					},
				});
				break;
			}
			default:
				break;
		}
	}

	handleResize(aspect: number) {
		const frustumSize = RENDERER_CONFIG.CAMERA.ORTHO.FRUSTRUM_SIZE;

		this.orthographicCamera.left = (-frustumSize * aspect) / 2;
		this.orthographicCamera.right = (frustumSize * aspect) / 2;
		this.orthographicCamera.top = frustumSize / 2;
		this.orthographicCamera.bottom = -frustumSize / 2;
		this.orthographicCamera.updateProjectionMatrix();

		this.perspectiveCamera.aspect = aspect;
		this.perspectiveCamera.updateProjectionMatrix();
	}

	dispose() {
		this.perspectiveControls.dispose();
	}

	export(): SerializedCameraController {
		return {
			userScreenshotPerspectiveCameras:
				this.userScreenshotPerspectiveCameras.map((camera) => camera.toJSON()),
		};
	}

	loadFromData(data: SerializedCameraController) {
		this.userScreenshotPerspectiveCameras =
			data.userScreenshotPerspectiveCameras.map((cameraData) => {
				const camera = this.loader.objectLoader.parse(
					cameraData
				) as PerspectiveCamera;
				return camera;
			});
	}
}
